import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { getTransportationData, saveTransportationForm } from '../../../../../../api/transitions';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import SkeletonBlock from '../../../SkeletonBlock';
import VehiclesForm from './VehiclesForm';
import DriversForm from './DriversForm';
import UsersForm from './UsersForm';
import OutsourcedCompaniesForm from './OutsourcedCompaniesForm';
import { flattenTransitionSubmitData } from '../../../formDataUtils';
import { trimData } from '../../../../../../utils/formatText';
import { TRANSPORTATION } from '../../../formQuestionsEnum';

const TransportationView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState();
  const facilityId = searchParams.get('facilityId');
  const [isLoading, setIsLoading] = useState(true);
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);

  const transportationPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.transportationFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getTransportationData(id, facilityId)
      .then((resp) => {
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load Transportation data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldNameMap = Object.fromEntries(Object.entries(TRANSPORTATION).map(([, v]) => [v.id, v.fieldName]));

    const mappedFields = {
      ...data?.fields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    setFormData({ transitionFacilityId: data?.transitionFacilityId, ...mappedFields });
  }, [data]);

  const submitForm = async (allData) => {
    const flattenData = flattenTransitionSubmitData(allData);
    const result = trimData(flattenData);

    await saveTransportationForm({
      id,
      sellerFacilityId: facilityId,
      ...result,
    })
      .then((resp) => {
        enqueueSnackbar('Transportation data saved successfully', { variant: 'success' });
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to save Transportation data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? (
    <SkeletonBlock height={600} sx={{ mt: 2 }} />
  ) : (
    <>
      <DriversForm permissions={transportationPermissions} />
      <VehiclesForm permissions={transportationPermissions} />
      <OutsourcedCompaniesForm permissions={transportationPermissions} />
      <UsersForm permissions={transportationPermissions} />
    </>
  );
});

export default TransportationView;

TransportationView.propTypes = {
  setFormData: PropTypes.func,
};
